/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import JSZip from 'jszip';
import DatePicker from 'react-datepicker';
import { API_ENDPOINTS } from '../../config/api';
import { services } from '../../services';
import '../../styles/datepicker-theme.css';

// Date <-> 'YYYY-MM-DD' helpers (local time, matches the backend day filter).
const toYMD = (d: Date | null): string => {
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
const fromYMD = (s: string): Date | null => (s ? new Date(`${s}T00:00:00`) : null);

// Toggle the in-app "Send an update" composer. Off for now — updates are sent
// externally from exported CSV batches.
const SHOW_COMPOSE = false;

// Rows per CSV file. >1 batch is delivered as a zip of batch files.
const EXPORT_BATCH_SIZE = 500;

interface Subscriber {
  _id: string;
  email: string;
  subscribedAt?: string;
  createdAt?: string;
}

const formatDateTime = (iso?: string) => {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString();
  } catch (_) {
    return iso;
  }
};

const NewsletterPage: React.FC = () => {
  const [subscribers, setSubscribers] = useState<Subscriber[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState<string | null>(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 50;

  // Date range filter (YYYY-MM-DD, inclusive)
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [exporting, setExporting] = useState(false);

  // Build the ?from=&to= query fragment.
  const buildDateQuery = (from: string, to: string) =>
    `${from ? `&from=${from}` : ''}${to ? `&to=${to}` : ''}`;

  const fetchSubscribers = async (
    page: number = currentPage,
    from: string = fromDate,
    to: string = toDate
  ) => {
    try {
      setLoading(true);
      setError(null);
      const url = `${API_ENDPOINTS.NEWSLETTER.LIST}?page=${page}&limit=${itemsPerPage}${buildDateQuery(from, to)}`;
      const response = await services.get(url);
      const data = response?.data;
      let rows: Subscriber[] = [];
      if (Array.isArray(data)) rows = data;
      else if (Array.isArray(data?.data)) rows = data.data;
      setSubscribers(rows);

      const total = Number(data?.total) || Number(data?.count) || rows.length;
      setTotalItems(total);
      setTotalPages(Math.max(1, Math.ceil(total / itemsPerPage)));
      setCurrentPage(page);
    } catch (err: any) {
      setError(err?.data?.message || 'Failed to load subscribers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscribers(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyDateFilter = () => fetchSubscribers(1, fromDate, toDate);
  const clearDateFilter = () => {
    setFromDate('');
    setToDate('');
    fetchSubscribers(1, '', '');
  };

  const handleSend = async () => {
    setStatus(null);
    setError(null);
    if (!subject.trim() || !message.trim()) {
      setError('Subject and message are required.');
      return;
    }
    if (!window.confirm(`Send this update to ${subscribers.length} subscriber(s)?`)) return;
    try {
      setSending(true);
      const response = await services.post(API_ENDPOINTS.NEWSLETTER.BROADCAST, {
        subject: subject.trim(),
        message: message.trim(),
      });
      const data = response?.data;
      setStatus(data?.message || 'Update sent to subscribers.');
      setSubject('');
      setMessage('');
    } catch (err: any) {
      setError(err?.data?.message || 'Failed to send the update.');
    } finally {
      setSending(false);
    }
  };

  const downloadBlob = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const toCsv = (rows: Subscriber[]) => {
    const header = 'Email,Subscribed At';
    const body = rows
      .map((s) => {
        const email = (s.email || '').replace(/"/g, '""');
        const date = s.subscribedAt || s.createdAt || '';
        return `"${email}","${date}"`;
      })
      .join('\n');
    return `${header}\n${body}`;
  };

  // Fetch every subscriber matching the current date range, across all pages.
  const fetchAllMatching = async (): Promise<Subscriber[]> => {
    const all: Subscriber[] = [];
    let page = 1;
    let total = Infinity;
    while (all.length < total) {
      const url = `${API_ENDPOINTS.NEWSLETTER.LIST}?page=${page}&limit=${EXPORT_BATCH_SIZE}${buildDateQuery(fromDate, toDate)}`;
      const response = await services.get(url);
      const data = response?.data;
      const rows: Subscriber[] = Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data)
        ? data
        : [];
      all.push(...rows);
      total = Number(data?.total) || Number(data?.count) || all.length;
      if (!rows.length) break;
      page += 1;
      if (page > 10000) break; // safety valve
    }
    return all;
  };

  // Export emails as CSV. <=500 -> single file. >500 -> zip of 500-row batches.
  // Exports ALL rows matching the current date range, not just the page shown.
  const handleExport = async () => {
    try {
      setExporting(true);
      setError(null);
      const rows = await fetchAllMatching();
      if (!rows.length) {
        setError('No subscribers to export for the selected range.');
        return;
      }

      const chunks: Subscriber[][] = [];
      for (let i = 0; i < rows.length; i += EXPORT_BATCH_SIZE) {
        chunks.push(rows.slice(i, i + EXPORT_BATCH_SIZE));
      }

      if (chunks.length === 1) {
        downloadBlob(new Blob([toCsv(chunks[0])], { type: 'text/csv;charset=utf-8' }), 'newsletter-subscribers.csv');
        return;
      }

      const zip = new JSZip();
      chunks.forEach((chunk, i) => {
        zip.file(`newsletter-batch-${i + 1}.csv`, toCsv(chunk));
      });
      const blob = await zip.generateAsync({ type: 'blob' });
      downloadBlob(blob, 'newsletter-subscribers.zip');
    } catch (err: any) {
      setError(err?.data?.message || 'Failed to export subscribers.');
    } finally {
      setExporting(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    backgroundColor: '#0B222C',
    border: '1px solid #095D52',
    color: '#ede8c5',
    borderRadius: '8px',
    padding: '10px 14px',
    outline: 'none',
  };

  const cardStyle: React.CSSProperties = {
    backgroundColor: '#0B222C',
    border: '2px solid #095D52',
    boxShadow:
      '0 8px 25px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(187, 228, 37, 0.2), inset 0 1px 0 rgba(187, 228, 37, 0.1)',
    backgroundImage:
      'linear-gradient(135deg, rgba(187, 228, 37, 0.03) 0%, rgba(9, 93, 82, 0.05) 50%, rgba(11, 34, 44, 0.98) 100%)',
  };

  return (
    <div className='space-y-6'>
      <div className='flex items-start justify-between gap-4 flex-wrap'>
        <div>
          <h3 className='text-2xl font-bold font-dm-serif' style={{ color: '#ede8c5' }}>
            Newsletter
          </h3>
          <p className='font-work-sans mt-1' style={{ color: '#b8c5c2' }}>
            {totalItems} {totalItems === 1 ? 'subscriber' : 'subscribers'} · export emails in batches
            of {EXPORT_BATCH_SIZE}
          </p>
        </div>

        {/* Date range filter + export */}
        <div className='flex items-end gap-2 flex-wrap'>
          <div className='flex flex-col'>
            <label className='text-xs font-work-sans mb-1' style={{ color: '#b8c5c2' }}>From</label>
            <DatePicker
              selected={fromYMD(fromDate)}
              onChange={(d: Date | null) => setFromDate(toYMD(d))}
              maxDate={fromYMD(toDate) || undefined}
              dateFormat='dd/MM/yyyy'
              placeholderText='dd/mm/yyyy'
              isClearable
            />
          </div>
          <div className='flex flex-col'>
            <label className='text-xs font-work-sans mb-1' style={{ color: '#b8c5c2' }}>To</label>
            <DatePicker
              selected={fromYMD(toDate)}
              onChange={(d: Date | null) => setToDate(toYMD(d))}
              minDate={fromYMD(fromDate) || undefined}
              dateFormat='dd/MM/yyyy'
              placeholderText='dd/mm/yyyy'
              isClearable
            />
          </div>
          <button
            onClick={applyDateFilter}
            disabled={loading}
            className='px-4 py-2 rounded-lg font-semibold'
            style={{ backgroundColor: '#095D52', color: '#ede8c5', border: '1px solid rgba(187,228,37,0.35)', whiteSpace: 'nowrap' }}
          >
            Apply
          </button>
          {(fromDate || toDate) && (
            <button
              onClick={clearDateFilter}
              disabled={loading}
              className='px-4 py-2 rounded-lg font-semibold'
              style={{ backgroundColor: 'rgba(9,93,82,0.25)', color: '#ede8c5', border: '1px solid rgba(187,228,37,0.25)', whiteSpace: 'nowrap' }}
            >
              Clear
            </button>
          )}
          <button
            onClick={handleExport}
            disabled={loading || exporting || totalItems === 0}
            className='px-5 py-2 rounded-lg font-semibold transition-all duration-200'
            style={{
              backgroundColor: totalItems === 0 ? 'rgba(9,93,82,0.4)' : '#095D52',
              color: '#ede8c5',
              border: '1px solid rgba(187, 228, 37, 0.4)',
              cursor: totalItems === 0 ? 'not-allowed' : 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
        </div>
      </div>

      {error && (
        <div
          className='px-4 py-3 rounded-md'
          style={{ backgroundColor: 'rgba(220,38,38,0.1)', color: '#fecaca', border: '1px solid rgba(220,38,38,0.3)' }}
        >
          {error}
        </div>
      )}
      {status && (
        <div
          className='px-4 py-3 rounded-md'
          style={{ backgroundColor: 'rgba(187,228,37,0.1)', color: '#d7f36b', border: '1px solid rgba(187,228,37,0.3)' }}
        >
          {status}
        </div>
      )}

      {/* Compose an update — hidden for now (SHOW_COMPOSE). Updates are sent externally from exported CSV batches. */}
      {SHOW_COMPOSE && (
      <div className='rounded-lg overflow-hidden shadow-lg p-6 space-y-4' style={cardStyle}>
        <div className='space-y-2'>
          <label className='text-sm font-semibold font-work-sans' style={{ color: '#ede8c5' }}>
            Subject
          </label>
          <input
            style={inputStyle}
            placeholder='e.g. New asset listing: Mayfair, London'
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
          />
        </div>
        <div className='space-y-2'>
          <label className='text-sm font-semibold font-work-sans' style={{ color: '#ede8c5' }}>
            Message
          </label>
          <textarea
            style={{ ...inputStyle, minHeight: '160px', resize: 'vertical' }}
            placeholder='Write your update. Line breaks are preserved. An unsubscribe link is added automatically.'
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
        </div>
        <div className='flex justify-end'>
          <button
            onClick={handleSend}
            disabled={sending}
            className='px-5 py-3 rounded-lg font-semibold transition-all duration-200'
            style={{
              backgroundColor: sending ? 'rgba(9,93,82,0.4)' : '#095D52',
              color: '#ede8c5',
              border: '1px solid rgba(187, 228, 37, 0.4)',
              cursor: sending ? 'not-allowed' : 'pointer',
            }}
          >
            {sending
              ? 'Sending…'
              : `Send to ${subscribers.length} subscriber${subscribers.length === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>
      )}

      {/* Subscriber list */}
      <div className='rounded-lg overflow-hidden shadow-lg' style={cardStyle}>
        <div className='overflow-x-auto'>
          <table className='min-w-full' style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
            <thead
              style={{
                backgroundColor: '#095D52',
                backgroundImage:
                  'linear-gradient(135deg, rgba(187, 228, 37, 0.1) 0%, rgba(9, 93, 82, 0.8) 100%)',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
              }}
            >
              <tr>
                <th className='px-6 py-4 text-left text-xs font-bold uppercase tracking-wider font-work-sans' style={{ color: '#ede8c5' }}>Email</th>
                <th className='px-6 py-4 text-left text-xs font-bold uppercase tracking-wider font-work-sans' style={{ color: '#ede8c5' }}>Subscribed</th>
              </tr>
            </thead>
            <tbody style={{ backgroundColor: '#0B222C' }}>
              {loading ? (
                <tr>
                  <td colSpan={2} className='px-4 py-8 text-center' style={{ color: '#b8c5c2' }}>Loading...</td>
                </tr>
              ) : subscribers.length === 0 ? (
                <tr>
                  <td colSpan={2} className='px-4 py-8 text-center' style={{ color: '#b8c5c2' }}>No subscribers yet.</td>
                </tr>
              ) : (
                subscribers.map((s, idx) => (
                  <tr
                    key={s._id}
                    className='transition-colors'
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#095D52')}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                    style={{ boxShadow: idx > 0 ? 'inset 0 1px 0 rgba(187, 228, 37, 0.2)' : undefined }}
                  >
                    <td className='px-6 py-4 whitespace-nowrap' style={{ color: '#ede8c5' }}>{s.email}</td>
                    <td className='px-6 py-4 whitespace-nowrap' style={{ color: '#b8c5c2' }}>{formatDateTime(s.subscribedAt || s.createdAt)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className='mt-4 flex items-center justify-between px-2'>
        <div className='text-sm font-work-sans' style={{ color: '#b8c5c2' }}>
          {totalItems > 0 ? (
            <>Showing {(currentPage - 1) * itemsPerPage + 1}–{Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems}</>
          ) : (
            <>No records</>
          )}
        </div>
        <div className='flex items-center space-x-2'>
          <button
            onClick={() => currentPage > 1 && fetchSubscribers(currentPage - 1)}
            disabled={currentPage <= 1 || loading}
            className='px-3 py-1 rounded-md'
            style={{
              backgroundColor: currentPage <= 1 ? 'rgba(9,93,82,0.15)' : '#095D52',
              color: '#ede8c5',
              border: '1px solid rgba(187,228,37,0.3)',
            }}
          >
            Prev
          </button>
          {Array.from({ length: totalPages }).slice(Math.max(0, currentPage - 3), Math.max(0, currentPage - 3) + 5).map((_, idx) => {
            const start = Math.max(1, currentPage - 2);
            const pageNum = Math.min(totalPages, start + idx);
            if (pageNum < 1) return null;
            return (
              <button
                key={`pg-${pageNum}`}
                onClick={() => fetchSubscribers(pageNum)}
                className='px-3 py-1 rounded-md'
                style={{
                  backgroundColor: pageNum === currentPage ? '#BAE325' : 'rgba(9,93,82,0.25)',
                  color: pageNum === currentPage ? '#0B222C' : '#ede8c5',
                  border: '1px solid rgba(187,228,37,0.35)',
                }}
              >
                {pageNum}
              </button>
            );
          })}
          <button
            onClick={() => currentPage < totalPages && fetchSubscribers(currentPage + 1)}
            disabled={currentPage >= totalPages || loading}
            className='px-3 py-1 rounded-md'
            style={{
              backgroundColor: currentPage >= totalPages ? 'rgba(9,93,82,0.15)' : '#095D52',
              color: '#ede8c5',
              border: '1px solid rgba(187,228,37,0.3)',
            }}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewsletterPage;
